import React, { FunctionComponent, useEffect } from 'react';
import { useAsyncCallback } from 'react-async-hook';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { cloneDeep, get, kebabCase } from 'lodash';
import {
  optionalEmailValidator,
  triggerValidation,
} from '../../../utils/validation';
import { Button } from '../../Input/Button/Button';
import { TextField } from '../../Input/TextField/TextField';
import { ErrorMessage } from '../../ErrorMessage/ErrorMessage';
import { ROUTES } from '../../../utils/routes';
import { Checkbox } from '../../Input/Checkbox/Checkbox';
import { FormLogoUpload } from '../../Input/ImageUpload/ImageUpload';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { addNewDealerDefaults } from '../../DealerLaunchDashboard/defaults';
import {
  CreateLaunchPadDealerRequest,
  LaunchPadService,
} from '../../../services/LaunchPadService';
import { DealerService } from '../../../services/DealerService';
import styles from '../styles.module.scss';

export const SupportNewDealer: FunctionComponent = () => {
  const history = useHistory();
  const form = useLaunchpadForm<CreateLaunchPadDealerRequest>();
  const createDealer = useAsyncCallback(LaunchPadService.createDealer);

  // by default derive the dealer id from the dealer name
  const dealerName = form.watch('dealerProps.name');
  useEffect(() => {
    if (dealerName && !form.formState.dirtyFields.dealerId) {
      form.setValue('dealerId', kebabCase(`${dealerName || ''}`));
      triggerValidation(form, 'dealerId');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerName]);

  // if a feature is selected, clear validation errors for all features
  const featuresOnline = form.watch('dealerProps.launchpad.features.online');
  const featuresInstore = form.watch('dealerProps.launchpad.features.instore');
  useEffect(() => {
    if (
      get(form.errors, 'dealerProps.launchpad.features.online') ||
      get(form.errors, 'dealerProps.launchpad.features.instore')
    ) {
      triggerValidation(form, 'dealerProps.launchpad.features.online');
      triggerValidation(form, 'dealerProps.launchpad.features.instore');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuresInstore, featuresOnline]);

  const onSubmit = async (data: CreateLaunchPadDealerRequest) => {
    createDealer.reset();

    // at least one product needs to be selected
    if (
      !data.dealerProps.launchpad?.features?.online &&
      !data.dealerProps.launchpad?.features?.instore
    ) {
      form.setError('dealerProps.launchpad.features.online', {
        message: 'At least one product should be selected',
      });
      form.setError('dealerProps.launchpad.features.instore', {
        message: 'At least one product should be selected',
      });
      return;
    }

    // the dealer id needs to be unique
    const duplicateId = await DealerService.getDealer(data.dealerId)
      .then(() => true)
      .catch(() => false);
    if (duplicateId) {
      form.setError('dealerId', {
        message: 'Another dealer has this id, please choose a different id',
      });
      return;
    }

    // force the dealer contact email to be lowercase as the backend only supports lowercase user emails
    const update = cloneDeep(data);
    if (update.userEmail) {
      update.userEmail = update.userEmail.toLowerCase();
    } else {
      // This property is optional, remove it if its set to empty string
      delete update.userEmail;
    }

    // create the dealer
    await createDealer
      .execute({
        ...update,
        dealerProps: addNewDealerDefaults(update.dealerProps, update.userEmail),
      })
      .then(() => {
        toast(
          'Dealer created successfully!' +
            (update.userEmail
              ? ` An email has been sent to the dealer contact ${update.userEmail}.`
              : ''),
        );
        history.push(ROUTES.SUPPORT_DASHBOARD.getPath());
      })
      .catch(() => toast('Failed to create dealer'));
  };

  return (
    <form className={styles.card} onSubmit={form.handleSubmit(onSubmit)}>
      <ErrorMessage error={createDealer.error} />
      <h1 className={styles.supportWelcome}>Create a new dealer</h1>
      <div>
        This will setup a new LaunchPad account and send the dealer an email
        that they can use to login
      </div>
      <TextField
        label="Dealer Name"
        name="dealerProps.name"
        form={form}
        validation={{ required: true }}
      />
      <TextField
        label="Dealer Id"
        name="dealerId"
        form={form}
        validation={{ required: true }}
      />
      <FormLogoUpload
        label="Dealer Logo (optional)"
        name="dealerLogoFile"
        form={form}
      />
      <TextField
        label="Dealer Contact First Name"
        name="userFirstName"
        form={form}
        validation={{ required: true }}
      />
      <TextField
        label="Dealer Contact Last Name"
        name="userLastName"
        form={form}
        validation={{ required: true }}
      />
      <TextField
        label="Dealer Contact Email"
        type="email"
        name="userEmail"
        form={form}
        validation={{ validate: optionalEmailValidator }}
      />
      <Checkbox
        label="Enable Online"
        name="dealerProps.launchpad.features.online"
        form={form}
      />
      <Checkbox
        label="Enable In-Store"
        name="dealerProps.launchpad.features.instore"
        form={form}
      />
      <Button
        label="Create"
        type="submit"
        variant="primary"
        loading={form.formState.isSubmitting}
      />
    </form>
  );
};
